import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TaskService from "../../../../services/TaskService";
import CategoryService from "../../../../services/CategoryService";
import { colors } from "../../../config/colors";
import TimeDropdown from '../../shared/TimeDropdown';
import { getChargeOutRateTitle } from "../../../utils/jobTitleLabel";

const CreateTask = () => {
  const navigate = useNavigate();
  const chargeOutRateTitle = getChargeOutRateTitle();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMinTimeOpen, setIsMinTimeOpen] = useState(false);
  const [isMaxTimeOpen, setIsMaxTimeOpen] = useState(false);
  const minTimePickerRef = useRef(null);
  const maxTimePickerRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  // Add draft-related state
  const DRAFT_STORAGE_KEY = "task_draft_data";
  const [showDraftModal, setShowDraftModal] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    cost: "",
    estimatedMinHours: "0",
    estimatedMinMinutes: "0",
    estimatedMaxHours: "0",
    estimatedMaxMinutes: "0",
    description: "",
    category: "",
  });

  // Add draft functionality methods
  const saveDraftToLocalStorage = () => {
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
        formData,
        timestamp: new Date().toISOString()
      }));
      toast.success("Draft saved successfully");
      navigate("/task-list");
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft");
    }
  };

  const loadDraftFromLocalStorage = () => {
    try {
      const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (draftData) {
        const parsedData = JSON.parse(draftData);
        setFormData(parsedData.formData);

        // Set category search term if category exists
        if (parsedData.formData.category) {
          const category = categories.find(cat => cat._id === parsedData.formData.category);
          if (category) {
            setCategorySearchTerm(category.categoryType || '');
          }
        }

        // Clear the draft after loading
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        toast.success("Draft loaded successfully");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error loading draft:", error);
      toast.error("Failed to load draft");
      return false;
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  };

  // Add handlers for the modal actions
  const handleLoadDraft = () => {
    loadDraftFromLocalStorage();
    setShowDraftModal(false);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftModal(false);
  };
  // Add validation message state
  const [validationMessage, setValidationMessage] = useState({
    title: '',
    cost: '',
    time: '',
    description: ''
  });

  const [wordCount, setWordCount] = useState(0);

  useEffect(() => {
    fetchCategories();
    if (id) {
      setIsEditMode(true);
      fetchTaskData();
    } else {
      // Only check for drafts in create mode, not edit mode
      const draftExists = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (draftExists) {
        setShowDraftModal(true);
      }
    }
    const handleClickOutside = (event) => {
      if (minTimePickerRef.current && !minTimePickerRef.current.contains(event.target)) {
        setIsMinTimeOpen(false);
      }
      if (maxTimePickerRef.current && !maxTimePickerRef.current.contains(event.target)) {
        setIsMaxTimeOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [id]);

  useEffect(() => {
    if (!id) {
      setIsEditMode(false);
      setFormData({
        title: "",
        cost: "",
        estimatedMinHours: "0",
        estimatedMinMinutes: "0",
        estimatedMaxHours: "0",
        estimatedMaxMinutes: "0",
        description: "",
        category: "",
      });
      setWordCount(0);
      // Optionally reset other state as needed
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await CategoryService.getAllCategories(false);
      if (response.success) {
        console.log("Categories:", response.data);
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    }
  };

  const fetchTaskData = async () => {
    setLoading(true);
    try {
      const response = await TaskService.getTaskById(id);
      if (response.data.success) {
        const taskData = response.data.data;
        setFormData({
          title: taskData.title,
          cost: taskData.cost.toString(),
          estimatedMinHours: Math.floor(taskData.estimatedMinTime / 60).toString(),
          estimatedMinMinutes: (taskData.estimatedMinTime % 60).toString(),
          estimatedMaxHours: Math.floor(taskData.estimatedMaxTime / 60).toString(),
          estimatedMaxMinutes: (taskData.estimatedMaxTime % 60).toString(),
          description: taskData.description,
          category: taskData.category?._id || "",
        });
      }
    } catch (error) {
      console.error("Error fetching task data:", error);
      toast.error("Failed to fetch task data");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error("Please enter a task title");
      return false;
    }
    if (formData.cost && (isNaN(formData.cost) || formData.cost < 0)) {
      toast.error("Please enter a valid cost");
      return false;
    }

    const minTime = convertToMinutes(formData.estimatedMinHours, formData.estimatedMinMinutes);
    const maxTime = convertToMinutes(formData.estimatedMaxHours, formData.estimatedMaxMinutes);

    // Check if minimum time is at least 1 minute
    if (minTime < 1) {
      toast.error("Estimated minimum time must be at least 1 minute");
      return false;
    }

    if (minTime === 0 && maxTime === 0) {
      toast.error("Please enter estimated time");
      return false;
    }

    if (minTime > maxTime) {
      toast.error("Minimum time cannot be greater than maximum time");
      return false;
    }

    if (!formData.description.trim()) {
      toast.error("Please enter a task description");
      return false;
    }
    if (!formData.category) {
      toast.error("Please select a category");
      return false;
    }
    return true;
  };

  const convertToMinutes = (hours, minutes) => {
    return Number(hours) * 60 + Number(minutes);
  };

  const showTemporaryError = (field, message) => {
    setValidationMessage(prev => ({
      ...prev,
      [field]: message
    }));

    setTimeout(() => {
      setValidationMessage(prev => ({
        ...prev,
        [field]: ''
      }));
    }, 2000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'title') {
      // Prevent starting with space
      if (value === ' ') {
        showTemporaryError('title', 'Cannot start with space');
        return;
      }
      // Only allow letters, numbers, spaces and basic punctuation
      const sanitizedValue = value.replace(/[^a-zA-Z0-9\s.,'-]/g, '');
      if (sanitizedValue !== value) {
        showTemporaryError('title', 'Special characters not allowed');
      }
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    } else if (name === 'cost') {
      if (value === '00') {
        return;
      }
      // Allow only numbers and decimal point
      if (!/^\d*\.?\d*$/.test(value)) {
        showTemporaryError('cost', 'Only numbers allowed');
        return;
      }

      // Prevent more than 2 decimal places
      if (value.includes('.') && value.split('.')[1].length > 2) {
        return;
      }

      const numValue = parseFloat(value);
      if (value !== '' && numValue < 0) {
        showTemporaryError('cost', 'Amount cannot be negative');
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (name === 'description') {
      const words = value.trim().split(/\s+/).filter(word => word.length > 0);
      const wordCount = words.length;

      if (wordCount > 100) {
        showTemporaryError('description', 'Description cannot exceed 100 words');
        return;
      }

      setWordCount(wordCount);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Add time validation to TimeDropdown onChange handlers
  const validateTimes = (minHours, minMinutes, maxHours, maxMinutes) => {
    const minTime = convertToMinutes(minHours, minMinutes);
    const maxTime = convertToMinutes(maxHours, maxMinutes);

    // Check if minimum time is at least 1 minute
    if (minTime < 1) {
      showTemporaryError('time', 'Estimated minimum time must be at least 1 minute');
      return false;
    }

    if (minTime > maxTime) {
      showTemporaryError('time', 'Minimum time cannot be greater than maximum time');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const taskData = {
        title: formData.title,
        cost: formData.cost ? Number(formData.cost) : 0,
        estimatedMinTime: convertToMinutes(
          formData.estimatedMinHours,
          formData.estimatedMinMinutes
        ),
        estimatedMaxTime: convertToMinutes(
          formData.estimatedMaxHours,
          formData.estimatedMaxMinutes
        ),
        description: formData.description,
        category: formData.category,
      };

      let response;
      if (isEditMode) {
        response = await TaskService.updateTask(id, taskData);
      } else {
        response = await TaskService.createTask(taskData);
      }

      if (response.success || response?.status === 200) {
        toast.success(isEditMode ? "Task updated successfully" : "Task created successfully");
        // Clear any draft if we successfully created a task
        if (!isEditMode) {
          clearDraft();
        }
        navigate("/task-list");
      } else {
        toast.error(response.message || (isEditMode ? "Failed to update task" : "Failed to create task"));
      }
    } catch (error) {
      console.error(isEditMode ? "Error updating task:" : "Error creating task:", error);

      // Handle API validation errors with detailed messages
      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        // Show the first validation error
        const firstError = error.response.data.details[0];
        toast.error(firstError.message || "Validation failed");

        // Set field-specific validation messages
        if (firstError.field) {
          setValidationMessage(prev => ({
            ...prev,
            [firstError.field]: firstError.message
          }));

          // Clear the validation message after 3 seconds
          setTimeout(() => {
            setValidationMessage(prev => ({
              ...prev,
              [firstError.field]: ''
            }));
          }, 3000);
        }
      } else {
        toast.error(error.response?.data?.message || (isEditMode ? "Failed to update task" : "Failed to create task"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate("/task-list");
  };

  const filteredCategories = categories?.filter(cat => (cat.categoryType.toLowerCase().includes(categorySearchTerm.toLowerCase()) && cat.isActive));

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10">
      {/* Floating Header */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-lg border-b border-accent-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full hover:bg-accent-light/20 transition-colors"
              >
                <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-xl font-semibold text-primary-headings">
                {isEditMode ? "Edit Task" : "Create Task"}
              </h1>
            </div>

            {/* Add top action buttons for draft functionality */}
            {/* {!isEditMode && (
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={saveDraftToLocalStorage}
                  className="px-6 py-2.5 border border-accent-light/30 rounded-lg text-accent-dark hover:bg-accent-light/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-light transition-colors duration-200"
                >
                  Save Draft & Close
                </button>
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-[60vh]">
            <div className="w-16 h-16 relative">
              <div className="w-16 h-16 rounded-full border-4 border-accent-light"></div>
              <div className="w-16 h-16 rounded-full border-4 border-primary-headings border-t-transparent animate-spin absolute top-0"></div>
            </div>
            <p className="mt-4 text-accent-dark">Creating task...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Task Details Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
              <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h2 className="text-lg font-semibold text-primary-headings">Task Details</h2>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Title Input */}
                  <div>
                    <label className="block text-sm font-medium text-accent-dark mb-2">
                      Title <span className="text-cta">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                        placeholder="Enter task title"
                      />
                      {validationMessage.title && (
                        <p className="mt-1 text-sm text-red-500 absolute">
                          {validationMessage.title}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Cost Input */}
                  <div>
                    <label className="block text-sm font-medium text-accent-dark mb-2">
                      {chargeOutRateTitle}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-dark">$</span>
                      <input
                        type="text"
                        name="cost"
                        value={formData.cost}
                        onChange={handleInputChange}
                        className="w-full pl-7 px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                        placeholder="Price to be Charged"
                      />
                      {validationMessage.cost && (
                        <p className="mt-1 text-sm text-red-500 absolute">
                          {validationMessage.cost}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Estimated Time */}
                  <div>
                    {/* <label className="block text-sm font-medium text-accent-dark mb-2">
                      Estimated Time <span className="text-cta">*</span>
                    </label> */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Minimum Time */}
                      <div>
                        <label className="block text-sm font-medium text-accent-dark mb-2">Estimated Minimum Time</label>
                        <div className="relative" ref={minTimePickerRef}>
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent pr-12"
                              placeholder="Select time"
                              value={formData.estimatedMinHours === "0" && formData.estimatedMinMinutes === "0"
                                ? ""
                                : `${formData.estimatedMinHours.length === 1 ? "0" + formData.estimatedMinHours : formData.estimatedMinHours}:${formData.estimatedMinMinutes.length === 1 ? "0" + formData.estimatedMinMinutes : formData.estimatedMinMinutes}`}
                              readOnly
                              onClick={() => setIsMinTimeOpen(!isMinTimeOpen)}
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark pointer-events-none">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            {isMinTimeOpen && (
                              <TimeDropdown
                                value={`${formData.estimatedMinHours.length === 1 ? "0" + formData.estimatedMinHours : formData.estimatedMinHours}:${formData.estimatedMinMinutes.length === 1 ? "0" + formData.estimatedMinMinutes : formData.estimatedMinMinutes}`}
                                onChange={(time) => {
                                  const [hours, minutes] = time.split(':');
                                  if (((!formData.estimatedMaxHours || formData.estimatedMaxHours === "0") && (!formData.estimatedMaxMinutes || formData.estimatedMaxMinutes === "0")) || validateTimes(hours, minutes, formData.estimatedMaxHours, formData.estimatedMaxMinutes)) {
                                    setFormData(prev => ({
                                      ...prev,
                                      estimatedMinHours: hours,
                                      estimatedMinMinutes: minutes
                                    }));
                                  }
                                }}
                                onClose={() => setIsMinTimeOpen(false)}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Maximum Time */}
                      <div>
                        <label className="block text-sm font-medium text-accent-dark mb-2">Estimated Maximum Time</label>
                        <div className="relative" ref={maxTimePickerRef}>
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent pr-12"
                              placeholder="Select time"
                              value={formData.estimatedMaxHours === "0" && formData.estimatedMaxMinutes === "0"
                                ? ""
                                : `${formData.estimatedMaxHours.length === 1 ? "0" + formData.estimatedMaxHours : formData.estimatedMaxHours}:${formData.estimatedMaxMinutes.length === 1 ? "0" + formData.estimatedMaxMinutes : formData.estimatedMaxMinutes}`}
                              readOnly
                              onClick={() => setIsMaxTimeOpen(!isMaxTimeOpen)}
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark pointer-events-none">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            {isMaxTimeOpen && (
                              <TimeDropdown
                                value={`${formData.estimatedMaxHours.length === 1 ? "0" + formData.estimatedMaxHours : formData.estimatedMaxHours}:${formData.estimatedMaxMinutes.length === 1 ? "0" + formData.estimatedMaxMinutes : formData.estimatedMaxMinutes}`}
                                onChange={(time) => {
                                  const [hours, minutes] = time.split(':');
                                  if (validateTimes(formData.estimatedMinHours, formData.estimatedMinMinutes, hours, minutes)) {
                                    setFormData(prev => ({
                                      ...prev,
                                      estimatedMaxHours: hours,
                                      estimatedMaxMinutes: minutes
                                    }));
                                  }
                                }}
                                onClose={() => setIsMaxTimeOpen(false)}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {validationMessage.time && (
                      <p className="mt-1 text-sm text-red-500">
                        {validationMessage.time}
                      </p>
                    )}
                  </div>

                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-accent-dark mb-2">
                      Category <span className="text-cta">*</span>
                    </label>
                    <div className="relative" ref={categoryDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent flex items-center justify-between"
                      >
                        <span className={formData.category ? "text-primary-headings" : "text-accent-dark/50"}>
                          {formData.category
                            ? categories.find((cat) => cat._id === formData.category)?.categoryType
                            : "Select category"}
                        </span>
                        <svg className="w-5 h-5 text-accent-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {isDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-accent-light/20">
                          <div className="p-2 border-b border-accent-light/20">
                            <input
                              type="text"
                              placeholder="Search categories..."
                              value={categorySearchTerm}
                              onChange={(e) => setCategorySearchTerm(e.target.value)}
                              className="w-full px-3 py-2 bg-accent-light/5 border border-accent-light/30 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent"
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {filteredCategories.map((category) => (
                              <button
                                key={category._id}
                                type="button"
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    category: category._id,
                                  }));
                                  setIsDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-2.5 text-left hover:bg-accent-light/5 transition-colors ${formData.category === category._id ? "bg-primary-light/10" : ""
                                  }`}
                              >
                                {category.categoryType}
                              </button>
                            ))}
                            {filteredCategories.length === 0 && (
                              <div className="text-center text-gray-500 py-2">
                                No categories found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description Input - Full Width */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-accent-dark mb-2">
                      Description <span className="text-cta">*</span>
                    </label>
                    <div className="relative">
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="4"
                        className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                        placeholder="Enter task description"
                      />
                      <div className="flex justify-between mt-1">
                        <span className="text-sm text-accent-dark">
                          {wordCount} / 100 words
                        </span>
                        {validationMessage.description && (
                          <p className="text-sm text-red-500">
                            {validationMessage.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 border-t border-accent-light/20 pt-6">
              <div className="flex justify-end items-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-cta text-white rounded-lg hover:bg-cta/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta transition-colors duration-200"
                >
                  {loading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update Task" : "Create Task")}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Draft Modal */}
      {showDraftModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true"></div>

            {/* Modal panel */}
            <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-light/20">
                    <svg className="w-6 h-6 text-primary-headings" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium leading-6 text-primary-headings">
                    Draft Found
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-accent-dark">
                      A draft task was found. Would you like to load it?
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={handleDiscardDraft}
                  className="px-4 py-2 text-sm font-medium text-accent-dark bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-light"
                >
                  No, Discard
                </button>
                <button
                  type="button"
                  onClick={handleLoadDraft}
                  className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md bg-primary-headings hover:bg-primary-headings/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                >
                  Yes, Load Draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateTask;
